import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Search, Trash2, Check, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronUp, ChevronsRight, ChevronDown, Eye, EyeOff, RefreshCw, Shield, FileText, Edit, Folder, Columns3, Loader2, CircleEllipsis, Pencil, Lock } from 'lucide-react';
import { hasPermission } from '../utils/permissionsHelper';
import { jwt_decode } from '../utils/jwt_decode';
import baseapi from '../services/baseapi';
import { showError, showSuccess } from '../utils/toast';
// import { CustomDropdown } from '../components/CustomDropdown ';
import { Modal } from '@mui/material';
import RolePermissionsSidebar from '../components/RolePermissionsSidebar';
import { useClientLevel } from '../hooks/useClientLevel';
import { ItemsPerPageDropdown } from '../components/ItemsPerPageDropdown ';

// Updated interface to match new API structure
interface Role {
    id: string;
    client_id: string;
    composite: boolean;
    client_name: string;
    description: string;
    is_deleted: boolean;
    is_system_role: any;
    organization_id: string;
    permissions: Record<string, string>; // e.g., {"system": "crud", "user_management": "cr"}
    role: string;
}

interface Category {
    id: string;
    name: string;
    description: string;
    models: string[];
    model_count: number;
    is_default: boolean;
    default_operations: string[];
}

interface Operation {
    id: string;
    name: string;
    description: string;
}

const Roles = () => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [categorySearch, setCategorySearch] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [roles, setRoles] = useState<Role[]>([]);
    const [deleteRole, setDeleteRole] = useState<Role | null>(null);
    const [open, setOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Actions dropdown menu state
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: string | number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition({ top: rect.bottom + 4, left: Math.max(10, rect.left - 110) });
        setOpenMenuId(prev => (prev === String(id) ? null : String(id)));
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (openMenuId && !target.closest(`[data-menu-id="${openMenuId}"]`) && !target.closest('[data-action-menu]')) {
                setOpenMenuId(null);
            }
        };
        const handleScroll = () => {
            if (openMenuId) setOpenMenuId(null);
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [openMenuId]);
    const [editid, seteditid] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const decodedToken = jwt_decode();
    const organizationId = decodedToken?.organization_id;
    const [selectedRoleForPermissions, setSelectedRoleForPermissions] = useState<Role | null>(null);

    // Pagination states
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);
    const startIndex = (currentPage - 1) * itemsPerPage;

    // Column visibility state
    const [showColumnFilter, setShowColumnFilter] = useState(false);
    type ColumnKey = 'role' | 'description' | 'actions' | 'type' | 'client_name';

    //permissions
    const canCreate = hasPermission('roles', 'create')
    const canDelete = hasPermission('roles', 'delete')
    const canUpdate = hasPermission('roles', 'update')

    const isFirstSearch = useRef(true);

    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
        role: true,
        description: true,
        type: true,
        client_name: true,
        actions: canDelete || canUpdate
    });

    // 1. Read level from localStorage
    const level = useClientLevel();
    const [_clients, setClients] = useState<{
        id: any; client_id: string; name: string; display_name: string
    }[]>([]);
    const [selectedClientId, setSelectedClientId] = useState('');

    // Fetch clients if global
    useEffect(() => {
        if (level?.name === 'global') {
            baseapi.get('/clients', { params: { limit: 1000, skip: 0 } })
                .then(res => {
                    if (res.data.status) {
                        setClients(res.data.data);
                    }
                })
                .catch(() => { });
        }
    }, [level]);

    useEffect(() => {
        fetchData(0, itemsPerPage);
    }, [level]);

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);

    // Re-fetch when debouncedSearch changes
    useEffect(() => {
        if (isFirstSearch.current) {
            isFirstSearch.current = false;
            return;
        }
        fetchData(0, itemsPerPage);
    }, [debouncedSearch]);

    // Add state for fetched categories and operations
    const [rolePermissions, setRolePermissions] = useState<{
        categories: Category[];
        operations: Operation[]
    }>({
        categories: [],
        operations: []
    });
    const [loadingRolePermissions, setLoadingRolePermissions] = useState(false);

    // Updated formData to store category-wise operations
    const [formData, setFormData] = useState({
        role: '',
        description: '',
        categoryOperations: {} as Record<string, string[]> // e.g., {"system": ["create", "read"], "user_management": ["read"]}
    });

    const fetchData = async (skip: number = 0, limit: number = 5, orderBy: Record<string, 'asc' | 'desc'>[] = [{ created: 'desc' }]) => {
        try {
            setLoading(true);
            const [sortEntry] = orderBy;
            const sort_by = Object.keys(sortEntry)[0];
            const sort_order = sortEntry[sort_by];
            let params: any = {}
            const scope_level = level;
            const clientIds = localStorage.getItem('client_ids');
            const queryObj: any = {};

            if (scope_level.name !== 'global') {
                queryObj.client_id = scope_level.id ? Number(scope_level.id) : '';
            } else {
                if (!clientIds || clientIds.length === 0) {
                    queryObj.is_global = true;
                }
            }
            if (debouncedSearch) {
                queryObj.$or = [
                    { role: debouncedSearch },
                    { description: debouncedSearch }
                ];
            }
            params = {
                query: JSON.stringify(queryObj),
                skip: skip,
                limit: limit,
                sort_by,
                sort_order
            };
            const response = await baseapi.get("/roles", { params });
            if (response.data.status) {
                const newRoles = response.data.data;
                setRoles(newRoles);
                setTotalPages(Math.ceil(response.data.total / limit));
                setTotalItems(response.data.total);
                const maxPage = Math.ceil(response.data.total / limit) || 1;
                if (currentPage > maxPage) {
                    setCurrentPage(maxPage);
                }
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'raised an error in console';
            showError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (showAddForm || showEditForm) {
            setLoadingRolePermissions(true);
            baseapi.get('/roles/rolepermissions')
                .then(res => {
                    if (res.data.status) {
                        const cats: Category[] = res.data.data.categories;
                        setRolePermissions({
                            categories: cats,
                            operations: res.data.data.operations
                        });
                        // Seed default categories into formData for the Add form
                        if (showAddForm) {
                            const defaultOps: Record<string, string[]> = {};
                            cats.forEach(cat => {
                                if (cat.is_default && cat.default_operations.length > 0) {
                                    defaultOps[cat.id] = [...cat.default_operations];
                                }
                            });
                            if (Object.keys(defaultOps).length > 0) {
                                setFormData(prev => ({
                                    ...prev,
                                    categoryOperations: { ...defaultOps, ...prev.categoryOperations }
                                }));
                            }
                        }
                    } else {
                        showError(res.data.message);
                    }
                })
                .catch(err => {
                    showError(err?.response?.data?.message || err.message || 'Failed to fetch role permissions');
                })
                .finally(() => setLoadingRolePermissions(false));
        }
    }, [showAddForm, showEditForm]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                const newDirection = prev.direction === 'asc' ? 'desc' : 'asc';
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: newDirection }]);
                return { key, direction: newDirection };
            } else {
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: 'asc' }]);
                return { key, direction: 'asc' };
            }
        });
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.role.trim()) newErrors.role = 'Role name is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';

        const selectedCategories = Object.keys(formData.categoryOperations);
        if (selectedCategories.length === 0) {
            newErrors.categories = 'At least one category with operations is required';
        } else {
            // Check if each selected category has at least one operation
            const hasEmptyCategory = selectedCategories.some(cat =>
                !formData.categoryOperations[cat] || formData.categoryOperations[cat].length === 0
            );
            if (hasEmptyCategory) {
                newErrors.operations = 'Each selected category must have at least one operation';
            }
        }

        // if (level?.name === 'global' && !selectedClientId) newErrors.client = 'Client is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: string, value: string | Record<string, string[]>) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // Helper function to convert operations array to permission string
    const convertOperationsToString = (operations: string[]): string => {
        const operationMap: Record<string, string> = {
            'create': 'c',
            'read': 'r',
            'update': 'u',
            'delete': 'd'
        };

        return operations
            .map(op => operationMap[op] || '')
            .filter(Boolean)
            .join('');
    };

    // Helper function to convert permission string to operations array
    const convertStringToOperations = (permissionStr: string): string[] => {
        const reverseMap: Record<string, string> = {
            'c': 'create',
            'r': 'read',
            'u': 'update',
            'd': 'delete'
        };

        return permissionStr.split('').map(char => reverseMap[char]).filter(Boolean);
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            setSubmitting(true);
            const clientIdToUse = level?.name === 'global' ? selectedClientId : level?.id || '';

            // Convert categoryOperations to the required format
            const permissions: Record<string, string> = {};
            Object.keys(formData.categoryOperations).forEach(category => {
                permissions[category] = convertOperationsToString(formData.categoryOperations[category]);
            });

            const params = {
                role: formData.role,
                role_id: formData.role,
                ...(clientIdToUse
                    ? { client_id: Number(clientIdToUse) }
                    : { is_global: true }
                ),
                description: formData.description,
                permissions: permissions,
                is_system_role: false,
                organization_id: Number(organizationId)
            };

            const response = await baseapi.post('/roles', params);
            if (response.data.status) {
                showSuccess(response.data.message);
                resetForm();
                setShowAddForm(false);
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
                setErrors({});
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'Something went wrong';
            showError(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    function getChangedFields(originalRole: Role, editForm: {
        role: string;
        description: string;
        client_id: string;
        categoryOperations: Record<string, string[]>;
    }): Partial<Role> {
        const changed: Partial<Role> = {};

        if (originalRole.role !== editForm.role) changed.role = editForm.role;
        if (originalRole.description !== editForm.description) changed.description = editForm.description;
        if (originalRole.client_id !== editForm.client_id) changed.client_id = editForm.client_id;

        const newPermissions: Record<string, string> = {};
        Object.keys(editForm.categoryOperations).forEach(cat => {
            newPermissions[cat] = convertOperationsToString(editForm.categoryOperations[cat]);
        });

        // Parse original permissions securely
        const originalPermissions = parsePermissions(originalRole.permissions);

        const permissionsChanged = (
            Object.keys(newPermissions).length !== Object.keys(originalPermissions).length
            || Object.keys(newPermissions).some(cat => newPermissions[cat] !== originalPermissions[cat])
            || Object.keys(originalPermissions).some(cat => !(cat in newPermissions))
        );

        if (permissionsChanged) {
            changed.permissions = newPermissions;
        }

        return changed;
    }




    const handleEditSubmit = async () => {
        if (!validateForm() || !editingRole) return;
        try {
            setSubmitting(true);
            const clientIdToUse = level?.name === 'global' ? selectedClientId : level?.id || '';

            // Get diff object containing only what changed
            const diff = getChangedFields(editingRole, {
                role: formData.role,
                description: formData.description,
                client_id: clientIdToUse,
                categoryOperations: formData.categoryOperations
            });


            const response = await baseapi.put(`/roles/${editid}`, diff);
            if (response.data.status) {
                showSuccess(response.data.message || 'Role updated successfully');
                resetForm();
                setShowEditForm(false);
                setEditingRole(null);
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
                setErrors({});
                seteditid('');
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'Something went wrong';
            showError(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    // Helper function to safely parse permissions
    const parsePermissions = (permissions: Record<string, string> | string | null | undefined): Record<string, string> => {
        if (!permissions) return {};
        if (typeof permissions === 'string') {
            try {
                return JSON.parse(permissions);
            } catch {
                return {};
            }
        }
        return typeof permissions === 'object' ? permissions : {};
    };


    // Updated handleEdit to parse the new permissions format
    const handleEdit = (role: Role) => {
        setCategorySearch('');
        setEditingRole(role);

        // Safely parse permissions
        const parsedPermissions = parsePermissions(role.permissions);

        // Convert permissions object to categoryOperations format
        const categoryOperations: Record<string, string[]> = {};
        Object.keys(parsedPermissions).forEach(category => {
            categoryOperations[category] = convertStringToOperations(parsedPermissions[category]);
        });

        // Ensure default categories are always present with at least their default_operations
        rolePermissions.categories.forEach(cat => {
            if (cat.is_default && cat.default_operations.length > 0) {
                if (!categoryOperations[cat.id]) {
                    categoryOperations[cat.id] = [...cat.default_operations];
                } else {
                    // Merge: make sure default ops are included
                    cat.default_operations.forEach(op => {
                        if (!categoryOperations[cat.id].includes(op)) {
                            categoryOperations[cat.id].push(op);
                        }
                    });
                }
            }
        });

        setFormData({
            role: role.role,
            description: role.description || '',
            categoryOperations: categoryOperations
        });
        seteditid(role.id);
        setSelectedClientId(role.client_id);
        setShowEditForm(true);
    };


    const resetForm = () => {
        setFormData({
            role: '',
            description: '',
            categoryOperations: {}
        });
        setCategorySearch('');
        setShowAddForm(false);
        setShowEditForm(false);
        setEditingRole(null);
        setErrors({});
        setSelectedClientId('');
    };

    // Updated handleDelete
    const handleDelete = async (_roleId: string, id: string) => {
        try {
            setLoading(true);

            // Optimistically update roles
            const updatedRoles = roles.filter(role => role.id !== id);
            setRoles(updatedRoles);

            const response = await baseapi.delete(`/roles/${id}`);

            if (response.data.status) {
                showSuccess("Role Deleted Successfully");
                await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
            } else {
                showError(response.data.message || 'Failed to delete role');
                await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'An error occurred';
            showError(errorMsg);
            await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        setIsRefreshing(true);
        setCurrentPage(1);
        setSearchTerm('');
        setDebouncedSearch('');
        fetchData();
        setTimeout(() => {
            setIsRefreshing(false);
        }, 1000);
    };

    // Pagination functions
    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchData((itemsPerPage * (page - 1)), itemsPerPage);
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setItemsPerPage(value);
        setCurrentPage(1);
        fetchData(0, value);
    };

    // Column visibility functions
    const handleColumnToggle = (column: ColumnKey) => {
        setVisibleColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    };

    const columnOptions: { key: ColumnKey; label: string }[] = [
        { key: 'role', label: 'Role' },
        { key: 'description', label: 'Description' },
        { key: 'type', label: 'Type' },
        { key: 'client_name', label: 'Client Name' },
        ...((canDelete || canUpdate)
            ? [{ key: 'actions' as ColumnKey, label: 'Actions' }]
            : []),
    ];





    // Toggle category selection (default categories cannot be deselected)
    const toggleCategory = (categoryId: string) => {
        const cat = rolePermissions.categories.find(c => c.id === categoryId);
        if (cat?.is_default) return; // locked — cannot deselect
        setFormData(prev => {
            const newCategoryOperations = { ...prev.categoryOperations };
            if (categoryId in newCategoryOperations) {
                delete newCategoryOperations[categoryId];
            } else {
                newCategoryOperations[categoryId] = [];
            }
            return { ...prev, categoryOperations: newCategoryOperations };
        });

        // Clear errors when category is toggled
        if (errors.categories || errors.operations) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.categories;
                delete newErrors.operations;
                return newErrors;
            });
        }
    };

    // Toggle operation for a specific category (locked default_operations cannot be removed)
    const toggleCategoryOperation = (categoryId: string, operationId: string) => {
        const cat = rolePermissions.categories.find(c => c.id === categoryId);
        // If this operation is a default_operation for a default category, block removal
        if (cat?.is_default && cat.default_operations.includes(operationId)) {
            const current = formData.categoryOperations[categoryId] || [];
            if (current.includes(operationId)) return; // already checked — don't uncheck
        }
        setFormData(prev => {
            const newCategoryOperations = { ...prev.categoryOperations };
            const operations = newCategoryOperations[categoryId] || [];

            if (operations.includes(operationId)) {
                // If unchecking read, also uncheck create, update, delete (unless locked by default_operations)
                if (operationId === 'read') {
                    const lockedOps = cat?.is_default ? (cat.default_operations || []) : [];
                    newCategoryOperations[categoryId] = operations.filter(op => {
                        if (op === 'read') return false;
                        if (['create', 'update', 'delete'].includes(op) && !lockedOps.includes(op)) {
                            return false;
                        }
                        return true;
                    });
                } else {
                    newCategoryOperations[categoryId] = operations.filter(op => op !== operationId);
                }
            } else {
                // When selecting 'create', 'update', or 'delete', auto-select 'read' as well
                const nextOps = [...operations, operationId];
                if (['create', 'update', 'delete'].includes(operationId) && !nextOps.includes('read')) {
                    nextOps.push('read');
                }
                newCategoryOperations[categoryId] = nextOps;
            }
            return { ...prev, categoryOperations: newCategoryOperations };
        });

        // Clear operation error when any operation is toggled
        if (errors.operations) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.operations;
                return newErrors;
            });
        }
    };

    const isCategorySelected = (categoryId: string) => {
        return categoryId in formData.categoryOperations;
    };

    const isCategoryOperationSelected = (categoryId: string, operationId: string) => {
        return formData.categoryOperations[categoryId]?.includes(operationId) || false;
    };




    const filteredCategories = rolePermissions.categories.filter(cat => {
        if (!categorySearch.trim()) return true;
        const query = categorySearch.toLowerCase().trim();
        const nameMatch = (cat.name || '').toLowerCase().includes(query) ||
            (cat.name || '').toLowerCase().replace(/_/g, ' ').includes(query);
        const descMatch = (cat.description || '').toLowerCase().includes(query);
        const idMatch = (cat.id || '').toLowerCase().includes(query);
        return nameMatch || descMatch || idMatch;
    });

    const targetCategories = categorySearch.trim() ? filteredCategories : rolePermissions.categories;
    const isAllSelected = targetCategories.length > 0 && targetCategories.every(cat =>
        (formData.categoryOperations[cat.id] || []).length === rolePermissions.operations.length
    );

    const renderFormSidebar = (isEdit: boolean = false) => (
        <div
            className={`fixed top-10 pb-25 right-0 sm:right-4 h-[calc(100vh-2rem)] w-full max-w-full sm:max-w-[480px] md:max-w-[520px] lg:max-w-[580px] transition-all duration-500 ease-in-out transform z-50 ${(isEdit ? showEditForm : showAddForm) ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
                }`}
        >
            <div className="h-full bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 rounded-2xl flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 sm:p-4 border-b border-gray-200 dark:border-gray-700/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 sm:p-3 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl shadow-lg">
                            {isEdit ? <Edit className="text-white" size={20} /> : <Plus className="text-white" size={20} />}
                        </div>
                        <div>
                            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                                {isEdit ? 'Edit Role' : 'Add New Role'}
                            </h2>
                            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                                {isEdit ? 'Update the role details' : 'Fill in the details to create a new role'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={resetForm}
                        className="p-2 sm:p-3 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl transition-colors cursor-pointer"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Form Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    <div className="space-y-4">
                        {/* Role Name */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                                <Shield size={18} className="text-orange-500" />
                                Role Name <span className="text-red-500 text-base">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.role}
                                onChange={(e) => handleInputChange('role', e.target.value)}
                                placeholder="Enter role name..."
                                className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/80 border rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 text-sm focus:border-orange-500 focus:bg-white dark:focus:bg-gray-700 outline-none transition-colors ${errors.role ? 'border-red-500 bg-red-500/5' : 'border-gray-300 dark:border-gray-600'}`}
                            />
                            {errors.role && (
                                <div className="flex items-center gap-2 mt-1 text-red-500 dark:text-red-400 text-sm">
                                    <AlertCircle size={14} />
                                    {errors.role}
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                                <FileText size={18} className="text-orange-500" />
                                Description <span className="text-red-500 text-base">*</span>
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                placeholder="Enter role description..."
                                className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/80 border rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 text-sm focus:border-orange-500 focus:bg-white dark:focus:bg-gray-700 outline-none transition-colors ${errors.description ? 'border-red-500 bg-red-500/5' : 'border-gray-300 dark:border-gray-600'}`}
                                rows={2}
                            />
                            {errors.description && (
                                <div className="flex items-center gap-2 mt-1 text-red-500 dark:text-red-400 text-sm">
                                    <AlertCircle size={14} />
                                    {errors.description}
                                </div>
                            )}
                        </div>

                        {/* Client Selection for Global Level */}
                        {/* {level?.name === 'global' && (
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                                    <Building size={18} className="text-orange-500" />
                                    Client
                                </label>
                                <CustomDropdown
                                    data={clients.map(client => ({
                                        label: client.display_name || client.name,
                                        value: client.id,
                                    }))}
                                    selectedValue={selectedClientId}
                                    onSelect={setSelectedClientId}
                                    placeholder="Select a client"
                                    className="w-full"
                                />
                                {!selectedClientId && errors.client && (
                                    <div className="flex items-center gap-2 mt-1 text-red-500 dark:text-red-400 text-sm">
                                        <AlertCircle size={14} />
                                        {errors.client}
                                    </div>
                                )}
                            </div>
                        )} */}

                        {/* Categories with Operations */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                    <Folder size={18} className="text-orange-500" />
                                    <span>Categories & Operations</span>
                                    <span className="text-red-500 text-base">*</span>
                                    {categorySearch.trim() && (
                                        <span className="text-xs font-normal text-gray-400 dark:text-gray-500">
                                            ({filteredCategories.length} of {rolePermissions.categories.length})
                                        </span>
                                    )}
                                </label>
                                {!loadingRolePermissions && rolePermissions.categories.length > 0 && (
                                    <div
                                        className="flex items-center gap-2 px-2 py-1 rounded bg-gray-100 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 hover:bg-gray-200/60 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                        onClick={() => {
                                            if (targetCategories.length === 0) return;
                                            const allOps = rolePermissions.operations.map(op => op.id);
                                            const newCategoryOperations = { ...formData.categoryOperations };
                                            if (!isAllSelected) {
                                                targetCategories.forEach(cat => {
                                                    newCategoryOperations[cat.id] = allOps;
                                                });
                                            } else {
                                                // Deselect target categories (preserving defaults)
                                                targetCategories.forEach(cat => {
                                                    if (cat.is_default && cat.default_operations.length > 0) {
                                                        newCategoryOperations[cat.id] = [...cat.default_operations];
                                                    } else {
                                                        delete newCategoryOperations[cat.id];
                                                    }
                                                });
                                            }
                                            handleInputChange('categoryOperations', newCategoryOperations);
                                            setErrors(prev => {
                                                const newErrors = { ...prev };
                                                delete newErrors.categories;
                                                delete newErrors.operations;
                                                return newErrors;
                                            });
                                        }}
                                        title={categorySearch.trim() ? "Select/Deselect all filtered categories" : "Select/Deselect all categories"}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isAllSelected}
                                            readOnly
                                            className="form-checkbox h-3.5 w-3.5 text-orange-500 rounded focus:ring-orange-500 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 pointer-events-none"
                                        />
                                        <span className="text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase">Select All</span>
                                    </div>
                                )}
                            </div>

                            {/* Category Search Input */}
                            {!loadingRolePermissions && rolePermissions.categories.length > 0 && (
                                <div className="relative mb-3">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4 pointer-events-none" />
                                    <input
                                        type="text"
                                        placeholder="Search categories..."
                                        value={categorySearch}
                                        onChange={(e) => setCategorySearch(e.target.value)}
                                        className="w-full pl-9 pr-8 py-2 bg-gray-50 dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:border-orange-500 focus:bg-white dark:focus:bg-gray-700 outline-none transition-colors"
                                    />
                                    {categorySearch && (
                                        <button
                                            type="button"
                                            onClick={() => setCategorySearch('')}
                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5 rounded cursor-pointer transition-colors"
                                            title="Clear search"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            )}

                            {loadingRolePermissions ? (
                                <div className="text-center text-gray-500 dark:text-gray-400 py-4">Loading...</div>
                            ) : (
                                <div className="space-y-3">
                                    {filteredCategories.length === 0 ? (
                                        <div className="text-center py-6 px-4 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50/50 dark:bg-gray-800/20">
                                            <Folder className="w-7 h-7 mx-auto text-gray-400 dark:text-gray-500 mb-1.5 opacity-40" />
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                No categories matching <span className="font-semibold text-gray-700 dark:text-gray-300">"{categorySearch}"</span>
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => setCategorySearch('')}
                                                className="mt-2 text-xs font-medium text-orange-500 hover:text-orange-600 cursor-pointer"
                                            >
                                                Clear search
                                            </button>
                                        </div>
                                    ) : (
                                        filteredCategories.map(category => {
                                        const isSelected = isCategorySelected(category.id);
                                        const isDefault = category.is_default;
                                        return (
                                            <div
                                                key={category.id}
                                                className={`border rounded-lg transition-all ${
                                                    isDefault
                                                        ? 'border-blue-400/60 bg-blue-50/40 dark:bg-blue-500/5 dark:border-blue-500/30'
                                                        : isSelected
                                                            ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-500/10'
                                                            : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/50'
                                                }`}
                                            >
                                                {/* Category Header */}
                                                <div
                                                    className={`px-4 py-3 flex items-center justify-between ${isDefault ? 'cursor-default' : 'cursor-pointer'}`}
                                                    onClick={() => !isDefault && toggleCategory(category.id)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        {isDefault ? (
                                                            <Lock className="w-4 h-4 text-blue-500 dark:text-blue-400 shrink-0" />
                                                        ) : (
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleCategory(category.id)}
                                                                className="form-checkbox h-4 w-4 text-orange-500 rounded focus:ring-orange-500 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 pointer-events-none"
                                                            />
                                                        )}
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-gray-900 dark:text-white font-medium capitalize">
                                                                    {category.name.replace(/_/g, ' ')}
                                                                </span>
                                                                {isDefault && (
                                                                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30">
                                                                        Default
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400">{category.description}</div>
                                                        </div>
                                                    </div>
                                                    {(isSelected || isDefault) && (
                                                        <ChevronDown className="w-4 h-4 text-gray-400" />
                                                    )}
                                                </div>

                                                {/* Operations Grid (shown when category is selected or default) */}
                                                {(isSelected || isDefault) && (
                                                    <div className="px-4 pb-3 pt-1 border-t border-gray-200 dark:border-gray-600/50">
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-semibold">Select Operations:</div>
                                                        {!isDefault && (
                                                            <div className="flex items-center mb-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={
                                                                        (formData.categoryOperations[category.id] || []).length === rolePermissions.operations.length
                                                                    }
                                                                    onChange={() => {
                                                                        const allOps = rolePermissions.operations.map((op) => op.id);
                                                                        setFormData((prev) => ({
                                                                            ...prev,
                                                                            categoryOperations: {
                                                                                ...prev.categoryOperations,
                                                                                [category.id]:
                                                                                    (prev.categoryOperations[category.id] || []).length === allOps.length ? [] : allOps,
                                                                            },
                                                                        }));
                                                                    }}
                                                                    className="form-checkbox h-4 w-4 text-green-500 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                                                                />
                                                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">All</span>
                                                            </div>
                                                        )}
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {rolePermissions.operations.map((operation) => {
                                                                const isOpSelected = isCategoryOperationSelected(category.id, operation.id);
                                                                const isLockedOp = isDefault && category.default_operations.includes(operation.id);
                                                                return (
                                                                    <div
                                                                        key={operation.id}
                                                                        className={`p-2 rounded border transition-all text-sm ${
                                                                            isLockedOp
                                                                                ? 'border-blue-300 dark:border-blue-500/40 bg-blue-50 dark:bg-blue-500/10 cursor-default'
                                                                                : isOpSelected
                                                                                    ? 'border-green-500 bg-green-50 dark:bg-green-500/20 cursor-pointer'
                                                                                    : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-transparent hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer'
                                                                        }`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (!isLockedOp) toggleCategoryOperation(category.id, operation.id);
                                                                        }}
                                                                        title={isLockedOp ? 'This permission is required by default and cannot be removed' : undefined}
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            {isLockedOp ? (
                                                                                <Lock className="w-3 h-3 text-blue-500 dark:text-blue-400 shrink-0" />
                                                                            ) : (
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isOpSelected}
                                                                                    onChange={() => toggleCategoryOperation(category.id, operation.id)}
                                                                                    className="form-checkbox h-3 w-3 text-green-500 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 pointer-events-none"
                                                                                />
                                                                            )}
                                                                            <span className={`capitalize ${
                                                                                isLockedOp
                                                                                    ? 'text-blue-700 dark:text-blue-300 font-semibold'
                                                                                    : 'text-gray-800 dark:text-white'
                                                                            }`}>{operation.name}</span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                        );
                                    })
                                )}
                                </div>
                            )}

                            {errors.categories && (
                                <div className="flex items-center gap-2 mt-2 text-red-500 dark:text-red-400 text-sm">
                                    <AlertCircle size={14} />
                                    {errors.categories}
                                </div>
                            )}
                            {errors.operations && (
                                <div className="flex items-center gap-2 mt-2 text-red-500 dark:text-red-400 text-sm">
                                    <AlertCircle size={14} />
                                    {errors.operations}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Form Footer */}
                <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700/50">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={isEdit ? handleEditSubmit : handleSubmit}
                            disabled={submitting}
                            className={`w-full sm:flex-1 flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-4 rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg text-sm cursor-pointer ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {submitting ? (
                                <RefreshCw className="w-5 h-5 animate-spin" />
                            ) : (
                                <Check size={18} />
                            )}
                            {submitting ? 'Submitting...' : (isEdit ? 'Update Role' : 'Create Role')}
                        </button>
                        <button
                            onClick={resetForm}
                            className="w-full sm:w-auto sm:px-6 py-3 bg-gray-100 dark:bg-gray-700/80 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-transparent rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 font-medium text-sm cursor-pointer"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="bg-gray-50 dark:bg-gray-900 p-3 min-h-screen pb-20 transition-colors duration-200">
            <div className="w-full mx-auto">
                {/* Top Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-1.5">
                        <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                            <Shield className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h1 className="font-bold text-lg text-gray-900 dark:text-gray-200 leading-none">Roles</h1>
                        </div>
                        {!loading &&
                            <div className='font-bold text-lg text-gray-600 dark:text-gray-100'>
                                ({totalItems})
                            </div>
                        }
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleRefresh}
                            className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>

                        <div className="relative w-full sm:w-56">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search roles..."
                                className="w-full bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 focus:border-orange-500 outline-none transition-all"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>

                        <div className="relative" data-col-menu>
                            <button
                                onClick={() => setShowColumnFilter(!showColumnFilter)}
                                className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-all flex items-center gap-2 cursor-pointer"
                            >
                                <Columns3 className="w-3.5 h-3.5" />
                                <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                            </button>
                            {showColumnFilter && (
                                <div className="absolute top-full mt-2 right-0 w-52 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto">
                                    <div className="px-3 py-1.5 text-[9px] font-bold text-gray-500 uppercase border-b border-gray-100 dark:border-gray-800 mb-1">Toggle Columns</div>
                                    {columnOptions.map((option) => (
                                        <button
                                            key={option.key}
                                            onClick={() => handleColumnToggle(option.key)}
                                            className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                                        >
                                            <div className="flex items-center gap-2.5">
                                                {visibleColumns[option.key] ? (
                                                    <Eye className="w-3 h-3 text-orange-500" />
                                                ) : (
                                                    <EyeOff className="w-3 h-3 text-gray-400 dark:text-gray-600" />
                                                )}
                                                {option.label}
                                            </div>
                                            <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${visibleColumns[option.key] ? 'bg-orange-600 border-orange-600' : 'border-gray-300 dark:border-gray-700'}`}>
                                                {visibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {canCreate && (
                            <button
                                onClick={() => {
                                    if (showAddForm) {
                                        resetForm();
                                    } else {
                                        setCategorySearch('');
                                        setShowAddForm(true);
                                    }
                                }}
                                className="flex items-center gap-1.5 bg-orange-600 rounded-lg px-4 py-1.5 text-[11px] text-white font-bold hover:bg-orange-700 shadow-lg shadow-orange-900/20 transition-all cursor-pointer"
                            >
                                <Plus className="w-4 h-4" />
                                Add Role
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-t-xl overflow-hidden shadow-2xl">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-200 dark:border-gray-700/50">
                                    {columnOptions.map(({ key, label }) => (
                                        visibleColumns[key] && (
                                            <th
                                                key={key}
                                                onClick={() => key !== 'actions' && handleSort(key)}
                                                className={`px-2 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest select-none whitespace-nowrap ${key === 'actions' ? 'text-center' : 'text-left cursor-pointer'}`}
                                            >
                                                <div className="flex items-center gap-1">
                                                    {label}
                                                    {key !== 'actions' && (
                                                        sortConfig?.key === key ? (
                                                            sortConfig.direction === 'asc' ? (
                                                                <ChevronUp size={12} />
                                                            ) : (
                                                                <ChevronDown size={12} />
                                                            )
                                                        ) : (
                                                            <ChevronDown size={12} className="opacity-40" />
                                                        )
                                                    )}
                                                </div>
                                            </th>
                                        )
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
                                {(loading || isRefreshing) ? (
                                    <tr>
                                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-gray-700 dark:text-gray-200 font-bold uppercase tracking-widest italic">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                                                Loading roles
                                            </div>
                                        </td>
                                    </tr>
                                ) : roles.length === 0 ? (
                                    <tr>
                                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-gray-500 font-bold uppercase tracking-widest italic">
                                            No roles found
                                        </td>
                                    </tr>
                                ) : (
                                    roles.map((role) => (
                                        <tr key={role.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-200 dark:border-gray-700/30">
                                            {visibleColumns.role && (
                                                <td className="px-2 py-0.5">
                                                    <button
                                                        onClick={() => setSelectedRoleForPermissions(role)}
                                                        className="text-[11px] text-orange-600 dark:text-orange-500 hover:text-orange-700 dark:hover:text-orange-400 font-bold font-mono transition-colors hover:underline hover:cursor-pointer decoration-orange-500/30 underline-offset-4"
                                                    >
                                                        {role.role}
                                                    </button>
                                                </td>
                                            )}
                                            {visibleColumns.description && (
                                                <td className="px-2 py-0.5">
                                                    <span className="text-[10px] text-gray-600 dark:text-gray-400 rounded transition-all">
                                                        {role.description}
                                                    </span>
                                                </td>
                                            )}

                                            {visibleColumns.type && (
                                                <td className="px-2 py-0.5">
                                                    <span
                                                        className={`text-[10px] px-2 py-0.5 rounded font-semibold transition-all ${role.is_system_role
                                                            ? "text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-600/20 border border-green-200 dark:border-green-500/20"
                                                            : "text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-600/20 border border-blue-200 dark:border-blue-500/20"
                                                            }`}
                                                    >
                                                        {role.is_system_role ? "System" : "Custom"}
                                                    </span>
                                                </td>
                                            )}
                                            {visibleColumns.client_name && (
                                                <td className="px-2 py-0.5">
                                                    <span className="text-[10px] text-gray-600 dark:text-gray-400 rounded transition-all">
                                                        {role.client_name}
                                                    </span>
                                                </td>
                                            )}
                                            {visibleColumns.actions && (
                                                <td className="px-2 py-0.5">
                                                    <button
                                                        data-menu-id={role.id}
                                                        disabled={role.is_system_role === true || String(role.is_system_role).toLowerCase() === 'true'}
                                                        onClick={(e) => !(role.is_system_role === true || String(role.is_system_role).toLowerCase() === 'true') && handleMenuOpen(e, role.id)}
                                                        title={role.is_system_role === true || String(role.is_system_role).toLowerCase() === 'true' ? "System role actions disabled" : ""}
                                                        className={`p-1.5 rounded-lg transition-colors ${role.is_system_role === true || String(role.is_system_role).toLowerCase() === 'true'
                                                            ? "text-gray-400 dark:text-gray-600 opacity-40 cursor-not-allowed"
                                                            : "text-gray-700 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                                            }`}
                                                    >
                                                        <CircleEllipsis className="w-4.5 h-4.5" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Table Footer */}
                <div className="bg-gray-50/80 dark:bg-gray-800/50 border rounded-b-xl border-gray-200 dark:border-gray-700 px-2 py-1 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <ItemsPerPageDropdown
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        />
                        <span className="text-[10px] text-gray-600 dark:text-gray-200 font-bold uppercase tracking-widest">
                            <span className="text-gray-900 dark:text-gray-200">{startIndex + 1}</span> - <span className="text-gray-900 dark:text-gray-200">{Math.min(startIndex + itemsPerPage, totalItems)}</span> OF {totalItems}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-gray-500 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"><ChevronsLeft className="w-4 h-4" /></button>
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-gray-500 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"><ChevronLeft className="w-4 h-4" /></button>
                        <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">{currentPage}</div>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 text-gray-500 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"><ChevronRight className="w-4 h-4" /></button>
                        <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="p-1.5 text-gray-500 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"><ChevronsRight className="w-4 h-4" /></button>
                    </div>
                </div>

                {/* Add Form */}
                {renderFormSidebar(false)}

                {/* Edit Form */}
                {renderFormSidebar(true)}

                {/* Background Overlay */}
                <div
                    className={`fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-xs transition-opacity duration-300 z-40 ${(showAddForm || showEditForm) ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    onClick={resetForm}
                />

                {/* Role Permissions Sidebar */}
                <RolePermissionsSidebar
                    isOpen={!!selectedRoleForPermissions}
                    onClose={() => setSelectedRoleForPermissions(null)}
                    roleName={selectedRoleForPermissions?.role || ''}
                    permissions={selectedRoleForPermissions?.permissions}
                />
            </div>

            {/* Click outside to close column filter dropdown */}
            {showColumnFilter && (
                <div
                    className="fixed inset-0 z-30"
                    onClick={() => setShowColumnFilter(false)}
                />
            )}

            {/* Delete Confirmation Modal */}
            <Modal open={open} onClose={() => setOpen(false)}>
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                w-[95%] sm:w-[500px] md:w-[550px] max-w-2xl bg-white dark:bg-gray-900/95 backdrop-blur-xl 
                border border-red-200 dark:border-red-400/20 shadow-2xl p-4 rounded-2xl text-gray-900 dark:text-white
                relative overflow-hidden">

                    {/* Subtle top border */}
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-400/40 to-transparent"></div>

                    {/* Header */}
                    <div className="flex items-center mb-4">
                        <div className="flex items-center justify-center mr-2 text-red-500 dark:text-red-400">
                            <Trash2 className='h-7 w-7'></Trash2>
                        </div>
                        <div className='flex items-center justify-center'>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Confirm Delete</h2>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="mb-4 bg-gray-50 dark:bg-gray-800/30 p-2 rounded-lg border border-gray-200 dark:border-gray-700/50">
                        <p className="text-gray-800 dark:text-white/90 mb-3 text-lg">
                            Are you sure you want to delete role <span className="font-semibold text-orange-600 dark:text-orange-400">{deleteRole?.role}</span>?
                        </p>
                        <p className="text-orange-600/80 dark:text-orange-400/70 text-sm">
                            This will permanently remove the role from your account.
                        </p>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4 justify-end">
                        <button
                            onClick={() => setOpen(false)}
                            className="px-6 py-3 border border-gray-300 dark:border-gray-600/50 text-gray-700 dark:text-gray-300 rounded-lg
                      hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white
                      transition-all duration-200 font-medium min-w-[100px] cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                if (deleteRole) {
                                    handleDelete(deleteRole.role, deleteRole.id);
                                    setOpen(false);
                                }
                            }}
                            className="px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold
                      hover:bg-orange-600 hover:shadow-lg hover:shadow-orange-500/20
                      transition-all duration-200 min-w-[100px] cursor-pointer"
                        >
                            Delete Role
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Actions Portal Menu */}
            {openMenuId && createPortal(
                (() => {
                    const activeRole = roles.find(r => String(r.id) === String(openMenuId));
                    if (!activeRole) return null;
                    return (
                        <div
                            data-action-menu
                            className="fixed w-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[9999] overflow-hidden py-1.5"
                            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                        >
                            {canUpdate && (
                                <button
                                    onClick={() => {
                                        handleEdit(activeRole);
                                        setOpenMenuId(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors cursor-pointer"
                                >
                                    <Pencil className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" /> Edit Role
                                </button>
                            )}
                            {canDelete && (
                                <button
                                    onClick={() => {
                                        setDeleteRole(activeRole);
                                        setOpen(true);
                                        setOpenMenuId(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-[11px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-3 font-bold cursor-pointer"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Delete Role
                                </button>
                            )}
                        </div>
                    );
                })(),
                document.body
            )}
        </div>
    );
};

export default Roles;
